import { REPLAY_COLUMNS, type Replay, type ReplayColumnName, type ReplayColumns, type ReplayRound } from "./replay.ts";

/**
 * One map played over two (or more) demos: the server crashed or the map was reset, and
 * the game went on in a new demo from a restored round, at the score it had. The parts
 * are joined into one replay: players matched by Steam ID (and teams by who is in them),
 * the rounds of the first part that the next one replays dropped, every tick of a later
 * part moved after the earlier one, and all indexes remapped.
 */

/** Ticks left between two parts, so a later part never overlaps an earlier one. */
const GAP_SECONDS = 10;

export class ReplayJoinError extends Error {}

function joinTwo(a: Replay, b: Replay): Replay {
  if (a.map !== b.map) throw new ReplayJoinError(`The parts are on different maps (${a.map}, ${b.map}).`);
  if (a.tickRate !== b.tickRate || a.sampleRate !== b.sampleRate) throw new ReplayJoinError("The parts were not recorded the same way.");

  // Players: b's matched to a's by Steam ID, new ones added.
  const players = a.players.map((p) => ({ ...p }));
  const bySteam = new Map(players.map((p, i) => [p.steamId, i]));
  // b's team numbers (0 = started T in b) may be the other way round: decided by who is in them.
  const votes = [0, 0];
  for (const p of b.players) {
    const i = bySteam.get(p.steamId);
    if (i !== undefined) votes[players[i].team === p.team ? 0 : 1]++;
  }
  const flip = votes[1] > votes[0];
  const teamOf = (t: 0 | 1): 0 | 1 => (flip ? ((1 - t) as 0 | 1) : t);
  const playerMap = b.players.map((p) => {
    const known = bySteam.get(p.steamId);
    if (known !== undefined) return known;
    players.push({ ...p, team: teamOf(p.team) });
    bySteam.set(p.steamId, players.length - 1);
    return players.length - 1;
  });
  if (players.length > 24) throw new ReplayJoinError("Too many players over the parts.");
  const pb = (i: number) => (i >= 0 ? (playerMap[i] ?? -1) : i);

  // Weapons: the union, b's indexes moved.
  const weapons = [...a.weapons];
  const weaponIndex = new Map(weapons.map((w, i) => [w, i]));
  const weaponMap = b.weapons.map((w) => {
    let i = weaponIndex.get(w);
    if (i === undefined) {
      i = Math.min(254, weapons.length);
      if (weapons.length < 255) {
        weapons.push(w);
        weaponIndex.set(w, i);
      }
    }
    return i;
  });
  const wb = (i: number) => weaponMap[i] ?? 0;

  // Rounds: a's up to the score b starts from; b's after, renumbered.
  const total = (r: ReplayRound) => r.score[0] + r.score[1];
  const bFirst = b.rounds[0];
  const resumedAt = bFirst ? total(bFirst) - (bFirst.winner ? 1 : 0) : Infinity;
  // A second demo read before 0.17.1 counts its score from 0–0 (it did not know the restored
  // score): it then goes on after all of the first's rounds, its score added to theirs.
  const unknownStart = resumedAt === 0 && a.rounds.length > 0;
  const kept = unknownStart ? a.rounds : a.rounds.filter((r) => total(r) <= resumedAt);
  const base = unknownStart ? (a.rounds.at(-1)!.score as [number, number]) : ([0, 0] as [number, number]);
  const lastKept = kept.at(-1);
  const offset = a.demoTicks + GAP_SECONDS * a.tickRate; // as joinReplays counts it
  const tb = (t: number) => t + offset;
  // a's frames up to the end of its last round kept: those of the rounds replayed go.
  const aFrames = lastKept ? lastKept.frameTo : 0;
  const frameOffset = aFrames;
  const roundMap = b.rounds.map((_, i) => kept.length + i);
  const rounds: ReplayRound[] = [
    ...kept.map((r, i) => ({ ...r, number: i + 1 })),
    ...b.rounds.map((r, i) => ({
      ...r,
      number: kept.length + i + 1,
      startTick: tb(r.startTick),
      liveTick: tb(r.liveTick),
      endTick: tb(r.endTick),
      postTick: tb(r.postTick),
      tTeam: teamOf(r.tTeam),
      score: (flip ? [r.score[1] + base[0], r.score[0] + base[1]] : [r.score[0] + base[0], r.score[1] + base[1]]) as [number, number],
      frameFrom: r.frameFrom + frameOffset,
      frameTo: r.frameTo + frameOffset,
    })),
  ];
  // Events of a's dropped rounds go with them.
  const aUntil = lastKept ? lastKept.postTick : -1;
  const inA = (t: number) => t <= aUntil;

  // Columns: a's frames as they are (players added get nothing), then b's, re-laid.
  const P = players.length;
  const Pa = a.players.length;
  const Pb = b.players.length;
  const frames = aFrames + b.frames;
  const columns = {} as Record<ReplayColumnName, ReplayColumns[ReplayColumnName]>;
  for (const name of Object.keys(REPLAY_COLUMNS) as ReplayColumnName[]) {
    const def = REPLAY_COLUMNS[name];
    const A = a.columns[name];
    const B = b.columns[name];
    const C = A.constructor as new (n: number) => typeof A;
    if (!def.perPlayer) {
      const out = new C(frames);
      out.set(A.subarray(0, aFrames) as never, 0);
      if (name === "frameTicks") for (let f = 0; f < b.frames; f++) out[aFrames + f] = tb(B[f]);
      else out.set(B as never, aFrames);
      columns[name] = out;
      continue;
    }
    const out = new C(frames * P);
    for (let f = 0; f < aFrames; f++) for (let p = 0; p < Pa; p++) out[f * P + p] = A[f * Pa + p];
    for (let f = 0; f < b.frames; f++)
      for (let p = 0; p < Pb; p++) {
        const v = B[f * Pb + p];
        out[(aFrames + f) * P + playerMap[p]] = name === "weapon" ? wb(v) : v;
      }
    columns[name] = out;
  }

  const both = !!(a.inventory && b.inventory);
  const bothEconomy = !!(a.economy && b.economy);
  return {
    ...a,
    format: Math.max(a.format, b.format) as Replay["format"],
    demoTicks: offset + b.demoTicks,
    writer: a.writer,
    teams: [
      { name: a.teams[0].name, score: lastScore(rounds, 0, a.teams[0].score) },
      { name: a.teams[1].name, score: lastScore(rounds, 1, a.teams[1].score) },
    ],
    players,
    rounds,
    weapons,
    kills: [...a.kills.filter((k) => inA(k.tick)), ...b.kills.map((k) => ({ ...k, tick: tb(k.tick), killer: pb(k.killer), victim: pb(k.victim), assister: pb(k.assister) }))],
    hurts: [...a.hurts.filter((h) => inA(h[0])), ...b.hurts.map((h) => [tb(h[0]), pb(h[1]), pb(h[2]), h[3], wb(h[4])] as Replay["hurts"][number])],
    blinds: [...a.blinds.filter((x) => inA(x[0])), ...b.blinds.map((x) => [tb(x[0]), pb(x[1]), pb(x[2]), x[3]] as Replay["blinds"][number])],
    grenades: [
      ...a.grenades.filter((g) => inA(g.throwTick)),
      ...b.grenades.map((g) => ({
        ...g,
        thrower: pb(g.thrower),
        throwTick: tb(g.throwTick),
        detonateTick: g.detonateTick === null || g.detonateTick === undefined ? g.detonateTick : tb(g.detonateTick),
        effectEndTick: g.effectEndTick === null || g.effectEndTick === undefined ? g.effectEndTick : tb(g.effectEndTick),
      })),
    ],
    bomb: [...a.bomb.filter((x) => inA(x.tick)), ...b.bomb.map((x) => ({ ...x, tick: tb(x.tick), player: pb(x.player) }))],
    chat: [...a.chat.filter((x) => inA(x.tick)), ...b.chat.map((x) => ({ ...x, tick: tb(x.tick), player: pb(x.player) }))],
    shots: [...shotsUntil(a.shots, aUntil), ...b.shots.map((v, i) => (i % 2 === 0 ? tb(v) : pb(v)))],
    inventory: both ? [...a.inventory!.filter((x) => inA(x[0])), ...b.inventory!.map(([t, p, ...w]) => [tb(t), pb(p), ...w.map(wb)])] : undefined,
    economy: bothEconomy
      ? [...a.economy!.filter((e) => e.round < kept.length), ...b.economy!.map((e) => ({ ...e, round: roundMap[e.round], player: pb(e.player), buys: e.buys.map(wb) }))]
      : undefined,
    frames,
    columns: columns as ReplayColumns,
  };
}

/** [tick, player] pairs up to a tick. */
function shotsUntil(shots: number[], until: number) {
  const out: number[] = [];
  for (let i = 0; i + 1 < shots.length; i += 2) if (shots[i] <= until) out.push(shots[i], shots[i + 1]);
  return out;
}

function lastScore(rounds: ReplayRound[], team: 0 | 1, fallback: number) {
  const last = rounds.at(-1);
  return last ? last.score[team] : fallback;
}

const starts = new WeakMap<Replay, number[]>();

/** The parts of one map, in the order they were played, as one replay. */
export function joinReplays(parts: Replay[]): Replay {
  if (parts.length === 0) throw new ReplayJoinError("Nothing to join.");
  let joined = parts[0];
  const at = [0];
  for (const part of parts.slice(1)) {
    at.push(joined.demoTicks + GAP_SECONDS * joined.tickRate);
    joined = joinTwo(joined, part);
  }
  starts.set(joined, at);
  return joined;
}

/** Where each part of a joined replay starts, in its ticks ([0] for a replay of one demo). */
export const partStarts = (r: Replay): number[] => starts.get(r) ?? [0];

/** The part of a joined replay a tick is in, and the tick in that part's demo. */
export function partAt(r: Replay, tick: number): { part: number; tick: number } {
  const at = partStarts(r);
  let part = 0;
  while (part + 1 < at.length && at[part + 1] <= tick) part++;
  return { part, tick: Math.max(0, tick - at[part]) };
}
